import { type FC } from 'react';
import { Navigate, Outlet, Route, Routes, useParams } from 'react-router';

import { FoldersPage } from '@/pages/insight/folders/folders-page';
import { InsightDetailWrapper } from '@/pages/insight-detail-wrapper';

import { InsightAddMoreDocuments } from './features/package-creation/insight/insight-add-more-documents';
import { SiteDueDiligenceDocumentsPage } from './features/package-creation/site-due-diligence/documents-page';
import { LocationPage } from './features/package-creation/site-due-diligence/location-page';
import { ReportsPage } from './features/package-creation/site-due-diligence/reports-page';
import { PageNotFound } from './layouts/page-not-found';
import { BuildingPackageDetailWrapper } from './pages/building-package/building-package-detail-wrapper';
import { BuildingPackageInputPage } from './pages/building-package/building-package-input-page';
import { BuildingPackageNavigationWrapper } from './pages/building-package/building-package-navigation-wrapper';
import { BuildingPackageResultPage } from './pages/building-package/building-package-result-page';
import { EngineeringDetailWrapper } from './pages/engineering/engineering-detail-wrapper';
import { EngineeringInputPage } from './pages/engineering/engineering-input-page';
import { EngineeringNavigationWrapper } from './pages/engineering/engineering-navigation-wrapper';
import { EngineeringResultPage } from './pages/engineering/engineering-result-page';
import { UserReviewWrapper } from './pages/engineering/load-calc';
import { LoadCalculationInProgress } from './pages/engineering/load-calc/processing-in-progress';
import { DashboardPage } from './pages/insight/dashboard/dashboard-page';
import { FindingsPage } from './pages/insight/findings/findings-page';
import { InsightDocumentsPage } from './pages/insight/insight-documents/insight-documents-page';
import { TenderDocumentsSummaryPage } from './pages/insight/summary/summary-page';
import { LuminaireScheduleDetailWrapper } from './pages/luminaire-schedule/luminaire-schedule-detail-wrapper';
import { LuminaireScheduleDocumentsPage } from './pages/luminaire-schedule/luminaire-schedule-documents-page';
import { LuminaireScheduleResultsPage } from './pages/luminaire-schedule/luminaire-schedule-results-page';
import { BidComparisonPage } from './pages/offer/bid-comparison-page';
import { OfferDashboardPage } from './pages/offer/offer-dashboard-page';
import { OfferDocumentsPage } from './pages/offer/offer-documents-page';
import { ReportGenerationDocumentsPage } from './pages/report-generation/documents-page';
import { ReportGenerationDetailWrapper } from './pages/report-generation/report-generation-detail-wrapper';
import { Results } from './pages/report-generation/results';
import { ScheduleCheckComparisonPage } from './pages/schedule-check/schedule-check-comparison-page';
import { ScheduleCheckDetailWrapper } from './pages/schedule-check/schedule-check-detail-wrapper';
import { StructuralChoicePage } from './pages/structural/structural-choice';
import { StructuralDetailWrapper } from './pages/structural/structural-detail-wrapper';
import { StructuralInputPage } from './pages/structural/structural-input-page';
import { StructuralNavigationWrapper } from './pages/structural/structural-navigation-wrapper';
import { StructuralResultPage } from './pages/structural/structural-result-page';
import { VersionCompareDashboardPage } from './pages/version-compare/version-compare-dashboard-page';
import { VersionCompareDocumentsPage } from './pages/version-compare/version-compare-documents-page';
import { VersionReconciliationTable } from './pages/version-compare/version-reconciliation-table';
import { Route as NavRoute } from './routes';

// ---------------------------------------------------------------------------
// Service route fragments — one component per route group
// ---------------------------------------------------------------------------

const EngineeringRoutes: FC = () => (
  <Routes>
    <Route path=":layoutId" element={<EngineeringDetailWrapper />}>
      <Route path={NavRoute.DASHBOARD} element={<EngineeringNavigationWrapper />} />
      <Route path={NavRoute.INPUT} element={<EngineeringInputPage />} />
      <Route path={NavRoute.RESULTS} element={<EngineeringResultPage />} />
      <Route path="*" element={<PageNotFound />} />
    </Route>
  </Routes>
);

const BuildingPackageRoutes: FC = () => (
  <Routes>
    <Route path=":buildingPackageId" element={<BuildingPackageDetailWrapper />}>
      {/* Historical packages can still advance to the supported load calculation workflow. */}
      <Route path={NavRoute.PROCESSING_SPEC_REQUIREMENTS} element={<UserReviewWrapper />} />
      <Route path={NavRoute.PROCESSING_LOAD_CALCULATION} element={<LoadCalculationInProgress />} />
      <Route path={NavRoute.USER_REVIEW_SPEC_REQUIREMENTS} element={<UserReviewWrapper />} />
      <Route path={NavRoute.DASHBOARD} element={<BuildingPackageNavigationWrapper />} />
      <Route path={NavRoute.INPUT} element={<BuildingPackageInputPage />} />
      <Route path={NavRoute.RESULTS} element={<BuildingPackageResultPage />} />
      <Route path="*" element={<PageNotFound />} />
    </Route>
  </Routes>
);

const StructuralRoutes: FC = () => (
  <Routes>
    <Route path=":structuralId" element={<StructuralDetailWrapper />}>
      <Route path={NavRoute.DASHBOARD} element={<StructuralNavigationWrapper />} />
      <Route path={NavRoute.INPUT} element={<StructuralInputPage />} />
      <Route path={NavRoute.RESULTS} element={<Outlet />}>
        <Route index element={<StructuralChoicePage />} />
        <Route path=":resultType" element={<StructuralResultPage />} />
      </Route>
      <Route path="*" element={<PageNotFound />} />
    </Route>
  </Routes>
);

const ScheduleCheckRoutes: FC = () => (
  <Routes>
    <Route path=":packageId" element={<ScheduleCheckDetailWrapper />}>
      <Route index element={<Navigate to={NavRoute.COMPARISON} replace />} />
      <Route
        path={NavRoute.DASHBOARD}
        element={<Navigate to={`../${NavRoute.COMPARISON}`} replace />}
      />
      <Route path={NavRoute.COMPARISON} element={<ScheduleCheckComparisonPage />} />
      <Route path={NavRoute.DOCUMENTS} element={<InsightDocumentsPage />} />
      <Route path="*" element={<PageNotFound />} />
    </Route>
  </Routes>
);

const LuminaireScheduleRoutes: FC = () => (
  <Routes>
    <Route path=":packageId" element={<LuminaireScheduleDetailWrapper />}>
      <Route index element={<Navigate to={NavRoute.RESULTS} replace />} />
      <Route
        path={NavRoute.DASHBOARD}
        element={<Navigate to={`../${NavRoute.RESULTS}`} replace />}
      />
      <Route path={NavRoute.RESULTS} element={<LuminaireScheduleResultsPage />} />
      <Route path={NavRoute.DOCUMENTS} element={<LuminaireScheduleDocumentsPage />} />
      <Route path="*" element={<PageNotFound />} />
    </Route>
  </Routes>
);

const ReportGenerationRoutes: FC = () => (
  <Routes>
    <Route path=":packageId" element={<ReportGenerationDetailWrapper />}>
      <Route index element={<Results />} />
      <Route path={NavRoute.DASHBOARD} element={<Results />} />
      <Route path={NavRoute.RESULTS} element={<Results />} />
      <Route path={NavRoute.DOCUMENTS} element={<ReportGenerationDocumentsPage />} />
      <Route path="*" element={<PageNotFound />} />
    </Route>
  </Routes>
);

const SiteDueDiligenceRoutes: FC = () => (
  <Routes>
    <Route path=":packageId" element={<InsightDetailWrapper />}>
      <Route index element={<Navigate to={NavRoute.REPORTS} replace />} />
      <Route
        path={NavRoute.DASHBOARD}
        element={<Navigate to={`../${NavRoute.REPORTS}`} replace />}
      />
      <Route path={NavRoute.DOCUMENTS} element={<SiteDueDiligenceDocumentsPage />} />
      <Route path={NavRoute.REPORTS} element={<ReportsPage />} />
      <Route path={NavRoute.LOCATION} element={<LocationPage />} />
      <Route path={NavRoute.UPLOAD} element={<InsightAddMoreDocuments />} />
      <Route path="*" element={<PageNotFound />} />
    </Route>
  </Routes>
);

const TenderDocumentsRoutes: FC = () => (
  <Routes>
    <Route path=":packageId" element={<InsightDetailWrapper />}>
      <Route index element={<Navigate to={NavRoute.DASHBOARD} replace />} />
      <Route path={NavRoute.SUMMARY} element={<TenderDocumentsSummaryPage />} />
      <Route path={NavRoute.DASHBOARD} element={<DashboardPage />} />
      <Route path={NavRoute.FINDINGS} element={<FindingsPage />} />
      <Route path={NavRoute.DOCUMENTS} element={<InsightDocumentsPage />} />
      <Route path={NavRoute.UPLOAD} element={<InsightAddMoreDocuments />} />
      <Route path="*" element={<PageNotFound />} />
    </Route>
  </Routes>
);

const RfpRfqReviewRoutes: FC = () => (
  <Routes>
    <Route path=":packageId" element={<InsightDetailWrapper />}>
      <Route index element={<Navigate to={NavRoute.DASHBOARD} replace />} />
      <Route path={NavRoute.DASHBOARD} element={<DashboardPage />} />
      <Route path={NavRoute.FINDINGS} element={<FindingsPage />} />
      <Route path={NavRoute.DOCUMENTS} element={<InsightDocumentsPage />} />
      <Route path="*" element={<PageNotFound />} />
    </Route>
  </Routes>
);

const WaterProcessSystemsAgentRoutes: FC = () => (
  <Routes>
    <Route path=":packageId" element={<InsightDetailWrapper />}>
      <Route index element={<Navigate to={NavRoute.DASHBOARD} replace />} />
      <Route path={NavRoute.DASHBOARD} element={<DashboardPage />} />
      <Route path={NavRoute.FINDINGS} element={<FindingsPage />} />
      <Route path={NavRoute.DOCUMENTS} element={<InsightDocumentsPage />} />
      <Route path={NavRoute.UPLOAD} element={<InsightAddMoreDocuments />} />
      <Route path="*" element={<PageNotFound />} />
    </Route>
  </Routes>
);

const DesignPackageReviewRoutes: FC = () => (
  <Routes>
    <Route path=":packageId" element={<InsightDetailWrapper />}>
      <Route index element={<Navigate to={NavRoute.DASHBOARD} replace />} />
      <Route path={NavRoute.DASHBOARD} element={<DashboardPage />} />
      <Route path={NavRoute.FINDINGS} element={<FindingsPage />} />
      <Route path={NavRoute.DOCUMENTS} element={<InsightDocumentsPage />} />
      <Route path="*" element={<PageNotFound />} />
    </Route>
  </Routes>
);

const DefaultInsightRoutes: FC = () => (
  <Routes>
    <Route path=":packageId" element={<InsightDetailWrapper />}>
      <Route path={NavRoute.DASHBOARD} element={<DashboardPage />} />
      <Route path={NavRoute.FINDINGS} element={<FindingsPage />} />
      <Route path={NavRoute.DOCUMENTS} element={<InsightDocumentsPage />} />
      <Route path={NavRoute.REPORTS} element={<ReportsPage />} />
      <Route path={NavRoute.CATEGORIZATION} element={<FoldersPage />} />
      <Route path={NavRoute.UPLOAD} element={<InsightAddMoreDocuments />} />
      <Route path="*" element={<PageNotFound />} />
    </Route>
  </Routes>
);

const VersionCompareRoutes: FC = () => (
  <Routes>
    <Route path=":packageId" element={<InsightDetailWrapper />}>
      <Route index element={<Navigate to={NavRoute.DASHBOARD} replace />} />
      <Route path={NavRoute.DASHBOARD} element={<VersionCompareDashboardPage />} />
      <Route path={NavRoute.FINDINGS} element={<VersionReconciliationTable />} />
      <Route path={NavRoute.DOCUMENTS} element={<VersionCompareDocumentsPage />} />
      <Route path="*" element={<PageNotFound />} />
    </Route>
  </Routes>
);

const OfferRoutes: FC = () => (
  <Routes>
    <Route path=":packageId" element={<InsightDetailWrapper />}>
      <Route path={NavRoute.DASHBOARD} element={<DashboardPage />} />
      <Route path={NavRoute.FINDINGS} element={<FindingsPage />} />
      <Route path={NavRoute.DOCUMENTS} element={<OfferDocumentsPage />} />
      <Route path={NavRoute.UPLOAD} element={<InsightAddMoreDocuments />} />
      <Route path="*" element={<PageNotFound />} />
    </Route>
  </Routes>
);

// New "Offer (Bid Review)" experience: per-contractor dashboard. The legacy
// "Bid Review" service (NavRoute.OFFER) keeps the generic DashboardPage.
const BidReviewRoutes: FC = () => (
  <Routes>
    <Route path=":packageId" element={<InsightDetailWrapper />}>
      <Route path={NavRoute.DASHBOARD} element={<OfferDashboardPage />} />
      <Route path={NavRoute.BID_COMPARISON} element={<BidComparisonPage />} />
      <Route path={NavRoute.FINDINGS} element={<FindingsPage />} />
      <Route path={NavRoute.DOCUMENTS} element={<OfferDocumentsPage />} />
      <Route path={NavRoute.UPLOAD} element={<InsightAddMoreDocuments />} />
      <Route path="*" element={<PageNotFound />} />
    </Route>
  </Routes>
);

// ---------------------------------------------------------------------------
// Service name → route component lookup
// ---------------------------------------------------------------------------

const SERVICE_ROUTES: Record<string, FC> = {
  [NavRoute.FLOOR_PLAN]: EngineeringRoutes,
  [NavRoute.CEILING_PLAN]: EngineeringRoutes,
  [NavRoute.PLANT_ROOM]: EngineeringRoutes,
  [NavRoute.SPACE_ANALYSIS]: EngineeringRoutes,
  [NavRoute.PARKING]: EngineeringRoutes,
  [NavRoute.REPORTS]: EngineeringRoutes,

  [NavRoute.CEILING_GRID]: BuildingPackageRoutes,
  [NavRoute.LOAD_CALCULATION]: BuildingPackageRoutes,

  [NavRoute.STRUCTURAL]: StructuralRoutes,
  [NavRoute.SCHEDULE_CHECK]: ScheduleCheckRoutes,
  [NavRoute.LUMINAIRE_SCHEDULE]: LuminaireScheduleRoutes,
  [NavRoute.REPORT_GENERATION]: ReportGenerationRoutes,
  [NavRoute.SITE_DUE_DILIGENCE]: SiteDueDiligenceRoutes,
  [NavRoute.TENDER_DOCUMENTS]: TenderDocumentsRoutes,
  [NavRoute.RFP_RFQ_REVIEW]: RfpRfqReviewRoutes,
  [NavRoute.WATER_PROCESS_SYSTEMS_AGENT]: WaterProcessSystemsAgentRoutes,
  [NavRoute.DESIGN_PACKAGE_REVIEW]: DesignPackageReviewRoutes,
  [NavRoute.VERSION_COMPARE]: VersionCompareRoutes,
  [NavRoute.OFFER]: OfferRoutes,
  [NavRoute.BID_REVIEW]: BidReviewRoutes,
};

export const RouteRenderer: FC = () => {
  const { serviceName } = useParams();
  const ServiceRoutes = SERVICE_ROUTES[serviceName ?? ''];
  return ServiceRoutes ? <ServiceRoutes /> : <DefaultInsightRoutes />;
};
